//@flow

import { decamelizeKeys, camelizeKeys } from 'humps';

import api from '../../lib/api';
import endpoints from '../../lib/endpoints';
import { FlightSession } from '../../screens/Flights/Common';
import {
  trackFlightSearch,
  trackFlightBookButtonPress,
} from './analyticsEventActions';
import { STORE_FLIGHTS_IN_FLIGHTS, STORE_COUNTRIES, STORE_ALTERNATIVE_FLIGHTS } from './types';

const convertIntoMs = dateString => Date.parse('1970/01/01 ' + dateString);
const byDepartureTime = (a, b) =>
  convertIntoMs(a.departureTime) - convertIntoMs(b.departureTime);


const isInEverestRegion = s => (s === 'KTM') || (s === 'LUA') || (s === 'RHP');

const showAlterativeFlights = searchParams => {
  const { toLocation, fromLocation } = searchParams;
  if (isInEverestRegion(toLocation) && isInEverestRegion(fromLocation)) return true;
}

const constructQueryForAlternativeFlights = params => {
  const { fromLocation, toLocation } = params;

  let from = fromLocation;
  let to = toLocation;

  // Assume toLocation is LUA in both cases.
  if (fromLocation === 'KTM') from = 'RHP';
  if (fromLocation === 'RHP') from = 'KTM';

  // Assume fromLocation is LUA in both cases.
  if (toLocation === 'KTM') to = 'RHP';
  if (toLocation === 'RHP') to = 'KTM';
  
  return {
    ...params,
    toLocation: to,
    fromLocation: from,
  };
}

export const searchFlights = (searchParams: Object) => async dispatch => {
  dispatch(trackFlightSearch({ startDate: searchParams.departureDate }));

  // Search and Show Alternative flights for Everest Region.
  if (showAlterativeFlights(searchParams)) {
    const alternativeParams = constructQueryForAlternativeFlights(searchParams);
    try {
      const {
        data: {
          flights: { outbound, inbound },
          session_id,
        },
      } = await api.postWithoutToken(
        endpoints.flights.search,
        decamelizeKeys(alternativeParams)
      ); 
  
      dispatch(
        storeAlternativeFlightSearchResults({
          inbound: camelizeKeys(inbound).sort(byDepartureTime),
          outbound: camelizeKeys(outbound).sort(byDepartureTime),
          sessionId: session_id,
        })
      );
    } catch (err) {
      dispatch(
        storeAlternativeFlightSearchResults({ inbound: null, outbound: null, sessionId: null })
      );
    }
  } else {
    // Clear Alternative Flights for other sectors
    dispatch(
      storeAlternativeFlightSearchResults({ inbound: null, outbound: null, sessionId: null })
    );
  }

  try {
    const {
      data: {
        flights: { outbound, inbound },
        session_id,
      },
    } = await api.postWithoutToken(
      endpoints.flights.search,
      decamelizeKeys(searchParams)
    );

    dispatch(
      storeFlightSearchResults({
        inbound: camelizeKeys(inbound).sort(byDepartureTime),
        outbound: camelizeKeys(outbound).sort(byDepartureTime),
        sessionId: session_id,
      })
    );
  } catch (err) {
    dispatch(
      storeFlightSearchResults({ inbound: [], outbound: [], sessionId: null })
    );
  }
};

export const storeFlightSearchResults = ({ inbound, outbound, sessionId }) => ({
  type: STORE_FLIGHTS_IN_FLIGHTS,
  inbound,
  outbound,
  sessionId,
});

export const storeAlternativeFlightSearchResults = (flightsAndSession) => ({
  type: STORE_ALTERNATIVE_FLIGHTS,
  alternativeFlights: flightsAndSession,
});

export const onBookFlights = (
  flight_id: string,
  return_flight_id: string | null,
  sessionId: string,
) => async (dispatch, getState) => {
  try {
    const { flights } = getState();

    // reserve the flights
    const { data } = await api.postWithoutToken('flights/reservation', {
      session_id: sessionId,
      flight_id,
      ...(return_flight_id ? { return_flight_id } : {}),
    });

    if (data.reservation_status === 'OK') {
      const { selectedInbound, selectedOutbound } = getSelectedFlights(
        flights,
        flight_id,
        return_flight_id
      );

      FlightSession.start();
      FlightSession.setInbound(selectedInbound);
      FlightSession.setOutbound(selectedOutbound);

      dispatch(trackFlightBookButtonPress());
    } else {
      // the component catches the errors and show the message
      throw new Error('Reservation Failed');
    }
  } catch (err) {
    console.log(err);
  }
};

export const fetchCountries = () => async dispatch => {
  try {
    const { data } = await api.get('places/countries');
    dispatch({ type: STORE_COUNTRIES, countries: data });
  } catch (err) {
    console.log(err);
  }
};

const getSelectedFlights = (flights, flightId, returnFlightId) => {
  const { inbound, outbound } = flights;

  let selectedOutbound = outbound.filter(o => o.flightId === flightId)[0];
  let selectedInbound = returnFlightId
    ? inbound.filter(o => o.flightId === returnFlightId)[0]
    : null;

  if (!selectedOutbound) {
    const { alternativeFlights: { inbound, outbound } } = flights;
    selectedOutbound = outbound.filter(o => o.flightId === flightId)[0];
    selectedInbound = returnFlightId
    ? inbound.filter(o => o.flightId === returnFlightId)[0]
    : null;
  }

  return { selectedInbound, selectedOutbound };
};



// WEBPACK FOOTER //
// ./src/redux/actions/FlightsUiActions.js